// eslint-disable-next-line no-redeclare
import { Box, Flex, Group, Select, Skeleton, Space, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { FC, useContext } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { useNavigate } from 'react-router-dom';
import { useElementSize } from '@mantine/hooks';
import { DatePicker } from '@mantine/dates';
import { addHours } from 'date-fns';
import ContactListItemDescription from '../Organismns/Contact/ContactListItemDescription';
import { Icon } from '../Atoms/Icons';
import ContactProviderHeader from '../Organismns/Contact/ContactProviderHeader';
import { DataS } from '../Service/DataS';
import 'dayjs/locale/de';
import { EventParticipationsContext } from '../Service/Context/EventParticipationContextV2';
import { DocumentS } from '../Templates/Documents/DocumentS';
import { DocumentContext } from '../Service/Context/DocumentContextV2';
import { ShippingType } from '../Service/DocumentT';
import { WindowContext } from '../Service/Context/WindowContext';
import { Contact } from '../Service/ContactS';
import { ThemeText } from '../Atoms/ThemeText';
import { openChangeCustomerModal } from './Document/DocumentChangeCustomerModal';
import EInvoiceViolationAlert from './Document/EInvoiceValidationAlert';

const DocumentHeadData: FC<{ customer: Contact | null }> = ({ customer }) => {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { xs } = useContext(WindowContext);
  const { ref, width } = useElementSize();
  const { loading, bookitupDocument, savePatch, handleChange } = useContext(DocumentContext);
  const eventParticipationsContext = useContext<any>(EventParticipationsContext);

  if (loading) {
    return <Skeleton width="100%" height={200} />;
  }

  if (!bookitupDocument) {
    return null;
  }

  const {
    originDocumentId,
    eventId,
    customerId,
    documentType,
    shippingType,
    date,
    validUntil,
    number,
    shippingDate,
    shippingEndDate,
  } = bookitupDocument ?? {};
  // const [localNumber, setLocalNumber] = useState(number);
  const hasShippingType = ['CONFIRMATION', 'INVOICE', 'PARTIAL_INVOICE', 'REMINDER'].includes(documentType ?? '');
  const isReminder = documentType === 'REMINDER';
  const fullVersion = width > 500;

  const modifyPickedDay = (_date: Date) => addHours(_date, 2);

  const openChangeCustomerModalHandler = () => {
    if (bookitupDocument) {
      openChangeCustomerModal({
        documentId: bookitupDocument.id,
        onChange: (newCustomerId) => handleChange({ customerId: newCustomerId }),
        eventParticipationsContext,
        initialCustomerId: bookitupDocument.customerId.toString(),
      });
    }
  };

  return (
    <div ref={ref} style={{ width: '100%' }}>
      {DocumentS.isInvoice(documentType) && <EInvoiceViolationAlert invoice={bookitupDocument} customer={customer} />}
      <Group position="apart" p={fullVersion ? 'xs' : 0}>
        <Box>
          <ContactProviderHeader />
          <Space h="xl" />
          <ContactListItemDescription
            withoutCountry={false}
            withoutPartner
            contactId={customerId}
            addressField
            editingContact={undefined}
            lastModified={undefined}
          />
          {!originDocumentId && (
            <Text
              mt="xs"
              color="blue"
              size="xs"
              variant="link"
              sx={{ cursor: 'pointer' }}
              onClick={openChangeCustomerModalHandler}
            >
              Kunde ändern
            </Text>
          )}
        </Box>
        <Box>
          <Flex wrap="wrap" direction={!xs ? 'column' : 'row'} gap="xs">
            {documentType !== 'REMINDER' && (
              <TextInput
                defaultValue={number}
                placeholder="-"
                label={<Text size="xs">{i18n._('document.no')}</Text>}
                onBlur={(e) => savePatch({ number: e.currentTarget.value })}
                sx={{ maxWidth: xs ? undefined : 125 }}
              />
            )}
            <DatePicker
              required
              clearable={false}
              label={
                <Text span size="xs">
                  Datum
                </Text>
              }
              placeholder="-"
              defaultValue={new Date(date)}
              onChange={(val) => val && savePatch({ date: modifyPickedDay(val) })}
              sx={{ maxWidth: xs ? undefined : 125 }}
            />
            {documentType === 'OFFER' && (
              <DatePicker
                clearable={false}
                label={<Text size="xs">{i18n._('document.validuntil')}</Text>}
                placeholder="-"
                value={validUntil ? new Date(validUntil) : validUntil}
                onChange={(val) => val && savePatch({ validUntil: modifyPickedDay(val) })}
                minDate={date ? new Date(date) : undefined}
                sx={{ maxWidth: xs ? undefined : 125 }}
              />
            )}
            {bookitupDocument && DocumentS.isInvoice(bookitupDocument.documentType) && (
              <DatePicker
                required
                clearable={false}
                label={
                  <Text span size="xs">
                    {i18n._('document.payuntil')}
                  </Text>
                }
                placeholder="-"
                value={validUntil ? new Date(validUntil) : validUntil}
                minDate={date ? new Date(date) : undefined}
                onChange={(val) => val && savePatch({ validUntil: modifyPickedDay(val) })}
                sx={{ maxWidth: xs ? undefined : 125 }}
              />
            )}
          </Flex>
        </Box>
      </Group>
      {hasShippingType && (
        <Flex mt={fullVersion ? 0 : 'sm'} justify={fullVersion ? 'flex-end' : 'flex-start'}>
          <Stack>
            <Tooltip disabled={!isReminder} label="Wert kann nur in der ursprünglichen Rechnung geändert werden.">
              <Select
                label={<Text size="xs">Versandart</Text>}
                data={DataS.shippingTypeOptions}
                value={shippingType}
                disabled={isReminder}
                onChange={(val) => val && savePatch({ shippingType: val as ShippingType })}
              />
            </Tooltip>
            {shippingType !== 'none' && (
              <Tooltip disabled={!isReminder} label="Wert kann nur in der ursprünglichen Rechnung geändert werden.">
                <Group position="apart" sx={{ alignItems: 'flex-end' }}>
                  <DatePicker
                    required
                    disabled={isReminder}
                    label={
                      <Text span size="xs">
                        Liefertermin
                      </Text>
                    }
                    placeholder="-"
                    value={shippingDate ? new Date(shippingDate) : shippingDate}
                    onChange={(val) => val && savePatch({ shippingDate: modifyPickedDay(val) })}
                    sx={{ maxWidth: 125 }}
                  />
                  {(shippingType === 'serviceperiod' || shippingType === 'deliveryperiod') && (
                    <>
                      <Text mb={8}>bis</Text>
                      <DatePicker
                        label={<></>}
                        disabled={isReminder}
                        placeholder="-"
                        value={shippingEndDate ? new Date(shippingEndDate) : shippingEndDate}
                        onChange={(val) => val && savePatch({ shippingEndDate: modifyPickedDay(val) })}
                        sx={{ maxWidth: 125 }}
                      />
                    </>
                  )}
                </Group>
              </Tooltip>
            )}
          </Stack>
        </Flex>
      )}
      {bookitupDocument && originDocumentId && (
        <ThemeText mt="xl" align="right">
          <Icon value="INFO" style={{ color: 'orange', paddingRight: 5 }} />
          Diese <Trans id={`document.${bookitupDocument.documentType.toLowerCase()}`} /> basiert auf{'  '}
          <span
            style={{ textDecoration: 'underline', color: '#8199AC', cursor: 'pointer' }}
            onClick={() => {
              const targetLink = eventId
                ? `/events/${eventId}/documents/${originDocumentId}`
                : `/documents/${originDocumentId}`;
              navigate(targetLink);
            }}
          >
            Dokument
          </span>
        </ThemeText>
      )}
    </div>
  );
};

export default DocumentHeadData;
